<template>
    <BaseCard
        as="article"
        :padding="isCollapsedReviewComplete ? 'sm' : 'md'"
        theme="cream"
        radius="lgr"
        data-testid="weekly-action-card"
        :data-action-id="action.id"
        class="flex min-w-0 flex-col gap-2"
        :class="{ 'px-5': isCollapsedReviewComplete }">
        <template v-if="isCollapsedReviewComplete">
            <div class="flex min-w-0 items-center justify-between gap-4">
                <div class="flex min-w-0 flex-col gap-1">
                    <h3 class="body-semibold text-ink">{{ action.title }}</h3>
                    <p v-if="reviewCompletionLabel" class="details-big text-ink-muted">
                        {{ reviewCompletionLabel }}
                    </p>
                </div>
                <BaseDropdownMenu :sections="reviewUndoMenuSections" :side-offset="6" @select="handleReviewUndoMenuSelect">
                    <template #trigger>
                        <JourneyButton
                            data-testid="weekly-action-review-status-menu"
                            theme="done"
                            size="sm"
                            class="inline-flex items-center gap-2"
                            :disabled="reviewDisabled"
                            :aria-busy="reviewDisabled"
                            :aria-label="`${action.title} is done. Open actions.`">
                            Done
                            <i class="bi bi-chevron-down text-xs" aria-hidden="true"></i>
                        </JourneyButton>
                    </template>
                </BaseDropdownMenu>
            </div>
        </template>

        <template v-else>
            <div class="flex min-w-0 flex-1 flex-col gap-1">
                <a
                    v-if="resourceTaxonomy"
                    data-testid="weekly-action-resource-link"
                    :href="resourceTaxonomy.href"
                    :target="resourceTaxonomy.target"
                    :rel="resourceTaxonomy.rel"
                    class="inline-flex items-center self-start gap-1 scaffold-details-small text-ink-muted underline underline-offset-4"
                    @click="handlePrimaryActionLinkClick">
                    <i class="bi text-[11px]" :class="taxonomy.icon" aria-hidden="true"></i>
                    {{ taxonomy.label }}
                </a>
                <WeeklyActionTypeChip v-else :channel="action.channel" appearance="journey" />
                <div class="flex min-w-0 items-start justify-between gap-4">
                    <h3 class="body-semibold text-ink min-w-0 flex-1">
                        {{ action.title }}
                    </h3>

                    <template v-if="variant === 'current' && action.status !== 'completed'">
                        <a
                            v-if="primaryAction?.kind === 'link' && isInAppStartAction"
                            data-testid="weekly-action-start-role-play"
                            :href="primaryAction.href"
                            :target="primaryAction.target"
                            :rel="primaryAction.rel"
                            class="start-link base-btn-secondary base-btn-sm inline-flex min-h-8 min-w-22 items-center justify-center rounded-lg body-small-semibold"
                            :aria-label="`Start ${action.title}`"
                            @click="handlePrimaryActionLinkClick">
                            Start
                        </a>
                        <JourneyButton
                            v-else-if="primaryAction?.kind === 'start_conversation'"
                            data-testid="weekly-action-start-conversation"
                            theme="secondary"
                            size="sm"
                            class="min-w-22"
                            :disabled="isConversationLaunching"
                            :aria-busy="isConversationLaunching"
                            @click="startConversation">
                            Start
                        </JourneyButton>
                        <JourneyButton
                            v-else
                            data-testid="weekly-action-discuss"
                            theme="neutral"
                            size="sm"
                            :disabled="isConversationLaunching"
                            :aria-busy="isConversationLaunching"
                            @click="startDiscussion">
                            Discuss
                        </JourneyButton>
                    </template>

                    <JourneyButton
                        v-if="variant === 'review'"
                        data-testid="weekly-action-review-complete"
                        theme="neutral"
                        size="sm"
                        class="order-1"
                        :disabled="reviewDisabled"
                        :aria-busy="reviewDisabled"
                        :aria-label="`Mark ${action.title} as complete`"
                        @click="emit('set-review-outcome', action.id, 'completed')">
                        I did this
                    </JourneyButton>
                </div>

                <p v-if="action.details" class="details-big text-ink-muted" :class="variant === 'review' ? 'min-h-9 line-clamp-2' : 'line-clamp-1'">
                    {{ action.details }}
                </p>
            </div>

            <template v-if="variant !== 'review'">
                <HorizontalDivider as="hr" spacing="none" class="my-1" />

                <div class="flex flex-wrap items-center justify-between gap-3">
                    <JourneyButton
                        data-testid="weekly-action-details-button"
                        theme="tertiary"
                        size="sm"
                        detail
                        :aria-label="`View details for ${action.title}`"
                        @click="emit('view-details', action.id)">
                        View details
                    </JourneyButton>

                    <div class="flex shrink-0 flex-wrap items-center gap-2">
                        <template v-if="variant === 'current'">
                            <BaseDropdownMenu v-if="action.status === 'completed'" :sections="undoMenuSections" :side-offset="6" @select="handleUndoMenuSelect">
                                <template #trigger>
                                    <JourneyButton
                                        data-testid="weekly-action-status-menu"
                                        theme="done"
                                        size="sm"
                                        class="inline-flex items-center gap-2"
                                        :disabled="isUpdating"
                                        :aria-busy="isUpdating"
                                        :aria-label="`${action.title} is done. Open actions.`">
                                        <i class="bi bi-check-circle-fill" aria-hidden="true"></i>
                                        Done
                                        <i class="bi bi-chevron-down text-xs" aria-hidden="true"></i>
                                    </JourneyButton>
                                </template>
                            </BaseDropdownMenu>
                            <JourneyButton
                                v-else
                                data-testid="weekly-action-status-button"
                                theme="tertiary"
                                size="sm"
                                detail
                                :disabled="isUpdating"
                                :aria-busy="isUpdating"
                                :aria-label="completionAriaLabel"
                                @click="emitStatusUpdate">
                                {{ completionLabel }}
                            </JourneyButton>
                        </template>

                        <JourneyButton
                            v-else
                            data-testid="toggle-weekly-action-selection"
                            :theme="isSelected ? 'selected' : 'neutral'"
                            size="sm"
                            class="inline-flex items-center gap-2"
                            :aria-pressed="isSelected"
                            :aria-label="isSelected ? `Committed, ${action.title}` : `Commit ${action.title} to your weekly actions`"
                            :disabled="selectionDisabled"
                            @click="emit('toggle-selection', action.id)">
                            <i v-if="isSelected" class="bi bi-check-lg" aria-hidden="true"></i>
                            {{ isSelected ? "Committed" : "Commit" }}
                        </JourneyButton>
                    </div>
                </div>
            </template>
        </template>
    </BaseCard>
</template>

<script setup lang="ts">
import type { DevelopmentActionReviewOutcome } from "~vue/api/generated/development-journeys";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import JourneyButton from "~vue/pages/developmentJourneys/DevelopmentJourneyButton.vue";
import WeeklyActionTypeChip from "~vue/pages/developmentJourneys/WeeklyActionTypeChip.vue";
import { computed } from "vue";

import { formatWeekdayInTimezone } from "./timezonePresentation";
import { getWeeklyActionTaxonomyPresentation } from "./weeklyActionPresentation";
import { getWeeklyActionPrimaryAction, handleWeeklyActionPrimaryLinkClick, logWeeklyActionPrimaryActionClicked } from "./weeklyActionPrimaryAction";
import type { WeeklyAction, WeeklyActionStatus } from "./weeklyActions";

const props = withDefaults(
    defineProps<{
        action: WeeklyAction;
        rolePlaySetupUrl: string;
        isCurrentWeek?: boolean;
        isUpdating?: boolean;
        isConversationLaunching?: boolean;
        timezone?: string;
        variant?: "current" | "review" | "selection";
        isSelected?: boolean;
        selectionDisabled?: boolean;
        reviewDisabled?: boolean;
        reviewOutcome?: DevelopmentActionReviewOutcome["status"];
    }>(),
    {
        isCurrentWeek: false,
        isUpdating: false,
        isConversationLaunching: false,
        variant: "current",
        isSelected: false,
        selectionDisabled: false,
        reviewDisabled: false,
        reviewOutcome: undefined,
    },
);

const emit = defineEmits<{
    "update-status": [actionId: string, status: WeeklyActionStatus];
    "view-details": [actionId: string];
    "toggle-selection": [actionId: string];
    "set-review-outcome": [actionId: string, outcome: DevelopmentActionReviewOutcome["status"]];
    "start-conversation": [initialUserMessage: string];
    "discuss-action": [actionId: string];
}>();

const primaryAction = computed(() => getWeeklyActionPrimaryAction(props.action, props.rolePlaySetupUrl));
const taxonomy = computed(() => getWeeklyActionTaxonomyPresentation(props.action.channel));
const resourceTaxonomy = computed(() => (taxonomy.value.kind === "resource" && primaryAction.value?.kind === "link" ? primaryAction.value : null));
const isInAppStartAction = computed(() => props.action.channel === "role_play_with_nadia");

const markUndoneSections = (disabled: boolean): DropdownMenuSection[] => [
    { id: "actions", items: [{ id: "mark-undone", type: "button", label: "Mark undone", icon: "bi bi-arrow-counterclockwise", disabled }] },
];
const undoMenuSections = computed(() => markUndoneSections(props.isUpdating));
const reviewUndoMenuSections = computed(() => markUndoneSections(props.reviewDisabled));

function handleUndoMenuSelect(item: DropdownMenuItem) {
    if (item.id === "mark-undone") {
        emitStatusUpdate();
    }
}

function handleReviewUndoMenuSelect(item: DropdownMenuItem) {
    if (item.id === "mark-undone") {
        emit("set-review-outcome", props.action.id, "not_completed");
    }
}

function emitStatusUpdate() {
    if (!props.isUpdating) {
        emit("update-status", props.action.id, nextStatus.value);
    }
}

function handlePrimaryActionLinkClick(event: MouseEvent) {
    const action = primaryAction.value;
    if (action?.kind === "link") {
        void handleWeeklyActionPrimaryLinkClick(action, event);
    }
}

function startConversation() {
    if (props.isConversationLaunching) {
        return;
    }
    const action = primaryAction.value;
    if (action?.kind !== "start_conversation") {
        return;
    }
    logWeeklyActionPrimaryActionClicked(action);
    emit("start-conversation", action.initialUserMessage);
}

function startDiscussion() {
    if (!props.isConversationLaunching) {
        emit("discuss-action", props.action.id);
    }
}

function assertNever(value: never): never {
    throw new Error(`Unhandled Development Journeys action status: ${value}`);
}

const completionLabel = computed(() => "Mark done");
const completionAriaLabel = computed(() => `${completionLabel.value}: ${props.action.title}`);
const isReviewComplete = computed(() => props.reviewOutcome === "completed");
const isCollapsedReviewComplete = computed(() => props.variant === "review" && isReviewComplete.value);
const reviewCompletionLabel = computed(() => {
    if (!isCollapsedReviewComplete.value) {
        return null;
    }
    if (props.action.status !== "completed") {
        return null;
    }
    const completedAt = new Date(props.action.status_changed_at);
    if (Number.isNaN(completedAt.getTime())) {
        return "done";
    }
    return `done ${formatWeekdayInTimezone(completedAt, props.timezone)}`;
});
const nextStatus = computed<WeeklyActionStatus>(() => {
    switch (props.action.status) {
        case "completed":
            return props.isCurrentWeek ? "pending" : "not_completed";
        case "not_completed":
        case "pending":
            return "completed";
        default:
            return assertNever(props.action.status);
    }
});
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .start-link {
        --color-button-secondary: var(--color-journey-start);
        --color-button-secondary-hover: var(--color-surface-hover);
        --color-button-secondary-text: var(--color-ink);
    }
}
</style>
